import React, { useMemo } from "react";
import { useHotelInfo } from "../../../hooks/useHotelInfo";
import { PageLoadingView } from "../../common/PageLoadingView";
import ImageUploader from "../../form/ImageUploader";

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col sm:flex-row sm:items-center py-2">
    <div className="w-40 text-sm text-gray-500 dark:text-gray-400">{label}</div>
    <div className="flex-1 text-sm text-gray-800 dark:text-gray-100 break-words">
      {value || "—"}
    </div>
  </div>
);

const HotelInfoData: React.FC = () => {
  const { data, loading, error, isUploading, progress, update, refresh } =
    useHotelInfo();

  const logoUrl = useMemo(() => {
    if (!data) return undefined;
    if (typeof data.logo === "string" && data.logo.startsWith("http")) {
      return data.logo;
    }
    return data._media?.logo?.signedUrl || undefined;
  }, [data]);

  const mapUrl = useMemo(() => {
    if (!data) return undefined;
    if (typeof data.mapfile === "string" && data.mapfile.startsWith("http")) {
      return data.mapfile;
    }
    return data._media?.mapfile?.signedUrl || undefined;
  }, [data]);

  if (loading) {
    return <PageLoadingView />;
  }

  if (error) {
    return (
      <div className="p-6 text-red-600 dark:text-red-400">{error.message}</div>
    );
  }

  if (!data) {
    return <div className="p-6">No hotel data found.</div>;
  }

  return (
    <div className="w-full flex flex-col gap-8 p-4 sm:p-6 text-gray-800 dark:text-white">
      <div>
        <h2 className="text-xl font-semibold mb-4">Hotel Information</h2>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          <InfoRow label="Name" value={data.name} />
          <InfoRow label="Phone" value={data.phone} />
          <InfoRow label="Email" value={data.email} />
          <InfoRow label="Website" value={data.website} />
          <InfoRow label="Timezone" value={data.timezone} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h3 className="text-lg font-medium mb-2">Logo</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            Upload a logo image for the hotel. Supported: JPG, PNG, WebP, GIF up
            to 10MB.
          </p>
          {data.hotelid ? (
            <ImageUploader
              entityType="hotel-logo"
              entityId={data.hotelid}
              currentImageUrl={logoUrl}
              onUploadComplete={async (key) => {
                await update({ logo: key });
                await refresh();
              }}
              onUploadError={(msg) => console.error("Logo upload error:", msg)}
              disabled={isUploading}
              showBackgroundToggle={true}
            />
          ) : (
            <div className="text-sm text-gray-500">Missing hotel ID.</div>
          )}
          {isUploading && progress && (
            <div className="text-xs text-gray-500 mt-2">
              Uploading: {progress.percentage}%
            </div>
          )}
        </div>

        <div>
          <h3 className="text-lg font-medium mb-2">Property Map</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            Upload a property map image for the hotel. Supported: JPG, PNG,
            WebP, GIF up to 10MB.
          </p>
          {data.hotelid ? (
            <ImageUploader
              entityType="hotel-map"
              entityId={data.hotelid}
              currentImageUrl={mapUrl}
              onUploadComplete={async (key) => {
                await update({ mapfile: key });
                await refresh();
              }}
              onUploadError={(msg) => console.error("Map upload error:", msg)}
              disabled={isUploading}
              showBackgroundToggle={true}
            />
          ) : (
            <div className="text-sm text-gray-500">Missing hotel ID.</div>
          )}
          {isUploading && progress && (
            <div className="text-xs text-gray-500 mt-2">
              Uploading: {progress.percentage}%
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HotelInfoData;
